/*
 * Copyright (C) 2026 Bohemian Miser <https://substack.com/@bohemianmiser>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import React, { memo, useState } from 'react';
import { MinimalNodeClassic } from './minimal-node-classic';
import { MinimalNodeModern } from './minimal-node-modern';
import { CLASSIC_CONTAINER, MODERN_CONTAINER, getLeafScaleOrigin } from '../../../lib/recipe-lanes/edge-anchors';
import {
    getNodeTheme,
    getNodeIconUrlAt,
    isIconSearchMatchedAt,
    currentShortlistIndex,
} from '@/lib/recipe-lanes/model-utils';
import { useRecipeStore } from '@/lib/stores/recipe-store';

export const MinimalNode: React.FC<any> = ({
    data, selected, isConnectable, id, dragging
}) => {
  const [isPivotMode, setIsPivotMode] = useState(false);
  const longPressTimer = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
      if (dragging) {
          if (longPressTimer.current) {
              clearTimeout(longPressTimer.current);
              longPressTimer.current = null;
          }
          setIsPivotMode(false);
          if (data.onSetLongPress) {
              data.onSetLongPress(false);
          }
      }
  }, [dragging, data]);
  // Subscribe to this node in the recipe store.
  // setShortlistIndex writes shortlistIndex directly onto graph.nodes[i], so
  // this selector re-renders only when this specific node changes.
  const storeNode = useRecipeStore(s => s.graph?.nodes.find(n => n.id === id));
  const openIconEditor = useRecipeStore(s => s.openIconEditor);
  // Global "leaf node size" setting (#155): scale leaf nodes (no incoming edge).
  // `data.isLeaf` is computed once during layout in react-flow-diagram.
  const leafNodeScale = useRecipeStore(s => s.leafNodeScale);
  // "Tick off" steps (#281): whether this node has been marked done by the cook.
  const isCompleted = useRecipeStore(s => s.completedNodeIds.includes(id));
  const toggleNodeCompleted = useRecipeStore(s => s.toggleNodeCompleted);

  // Use storeNode when available (it has up-to-date shortlistIndex after cycling).
  // Fall back to data prop for nodes not yet in the store.
  const node = storeNode ?? data;
  const iconTheme = getNodeTheme(data);

  const currentIndex = Math.max(0, currentShortlistIndex(node));
  const iconUrl = getNodeIconUrlAt(node, currentIndex);
  const isSearchMatched = isIconSearchMatchedAt(node, currentIndex);

  const touchStartPos = React.useRef<{x: number, y: number} | null>(null);

  const handlePointerDown = (e: React.PointerEvent) => {
      // Only listen to touch or pen, ignore mouse for long-press pivot
      if (e.pointerType === 'mouse') return;
      
      touchStartPos.current = { x: e.clientX, y: e.clientY };
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      longPressTimer.current = setTimeout(() => {
          setIsPivotMode(true);
          if (data.onSetLongPress) data.onSetLongPress(true, id);
          if (navigator.vibrate) navigator.vibrate(50);
      }, 300);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
      if (!longPressTimer.current || !touchStartPos.current) return;
      const dx = e.clientX - touchStartPos.current.x;
      const dy = e.clientY - touchStartPos.current.y;
      if (Math.sqrt(dx*dx + dy*dy) > 10) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
      }
  };

  const handlePointerUpOrCancel = () => {
      if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
      }
      setTimeout(() => setIsPivotMode(false), 500);
  };

  const handleDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (data.onDelete) data.onDelete();
  };

  const handleEditIcon = (e: React.MouseEvent) => {
      e.stopPropagation();
      openIconEditor(id);
  };

  const handleToggleCompleted = (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleNodeCompleted(id);
  };

  const handlers = {
      onEditIcon: handleEditIcon,
      onDelete: handleDelete,
      onToggleCompleted: handleToggleCompleted,
      onPointerDownCapture: handlePointerDown,
      onPointerMoveCapture: handlePointerMove,
      onPointerUpCapture: handlePointerUpOrCancel,
      onPointerCancelCapture: handlePointerUpOrCancel
  };

  // NOTATION always renders classic, whatever the icon-theme preference says —
  // the same pin as textPos (see effectiveTextPos in react-flow-diagram.tsx).
  // The notation layout reserves space from the CLASSIC geometry (56/80px
  // containers, label centred below); the modern theme draws 80/120px
  // containers and hangs a ~144px text bubble off an action's LEFT side, none
  // of which the layout knows about, so picking "Modern" in notation overlapped
  // every step with its neighbour. Modern-theme notation is future work: it
  // needs its own reserved geometry in notation-metrics.ts.
  const useModern =
      (iconTheme === 'modern' || iconTheme === 'modern_clean') && !data.isNotationView;
  const inner = useModern
      ? <MinimalNodeModern data={data} selected={selected} isCompleted={isCompleted} isPivotMode={isPivotMode} iconUrl={iconUrl} isSearchMatched={isSearchMatched} handlers={handlers} />
      : <MinimalNodeClassic data={data} selected={selected} isCompleted={isCompleted} isPivotMode={isPivotMode} iconUrl={iconUrl} isSearchMatched={isSearchMatched} handlers={handlers} />;

  // Scale leaf nodes down when the global slider is below 100%. The transform
  // origin is pinned to the HANDLE point (top-center of the icon container) so
  // ReactFlow's measured handle position stays valid at every scale — CSS
  // transforms don't trigger re-measurement, and scaling about the node center
  // used to drag the icon (and every edge ending) downward. See edge-anchors.ts.
  if (data.isLeaf && leafNodeScale < 1) {
      const containerSize = useModern
          ? MODERN_CONTAINER[data.type === 'ingredient' ? 'ingredient' : 'action']
          : CLASSIC_CONTAINER[data.type === 'ingredient' ? 'ingredient' : 'action'];
      const origin = getLeafScaleOrigin(data.textPos || 'bottom', containerSize);
      return <div style={{ transform: `scale(${leafNodeScale})`, transformOrigin: origin }}>{inner}</div>;
  }
  return inner;
};

export default memo(MinimalNode);
